import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import {
  Breadcrumb,
  Button,
  MenuButton,
  Modal,
  ResponsivePrimaryNav,
  SearchResultNavigation,
} from "../../../components/ui";
import {
  FETCH_CONSIGNMENT as fetchConsignment,
  FETCH_CONSIGNMENTS_LIST as fetchConsignmentsList,
} from "../../../graphql/query/Consignment";
import useSearchResultNavigation from "../../../hooks/useSearchResultNavigation";
import useUser from "../../../hooks/useUser";
import routes from "../pages/Wizard/routes";
import AdhocReportBody from "./Actions/AdhocReportBody";
import CopyDescription from "./Actions/CopyDescription";
import CreateInvoice from "./Actions/CreateInvoice";
import CreateJob from "./Actions/CreateJob";
import CreateShipment from "./Actions/CreateShipment";
import exportModule from "./Actions/ExportModule";
import ConsignmentSearch from "./ConsignmentSearch";
import NewConsignment from "./NewConsignment";
import { buildConsignmentNavigationTargetUrl } from "./searchResultNavigation";
import {
  consignmentFilterStore,
  consignmentNavigationStore,
} from "./useConsignmentFilters";
import ExtendConsignment from "./Actions/ExtendConsignment";
import { clearSelectionState } from "../../../util/clearSelectionState";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";

const PrimaryNav = (props = {}) => {
  const { formik } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const search = consignmentFilterStore.get();
  const { hasPermission } = useUser();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const pathnames = location?.pathname.split("/");
  const consignmentId = pathnames?.length > 2 ? pathnames[2] : null;
  const consignmentIdNumber = consignmentId ? Number(consignmentId) : null;

  const { data: { consignment } = {} } = useQuery(fetchConsignment({}), {
    skip: !consignmentId,
    variables: { id: +consignmentId },
  });

  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get("status") || "all";
  const listContext = useMemo(
    () => ({ locationSearch: location.search }),
    [location.search],
  );
  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  } = useSearchResultNavigation({
    activeSubPage: route?.href || "form",
    buildTargetUrl: buildConsignmentNavigationTargetUrl,
    dataKey: "consignmentsV2",
    fetchQuery: fetchConsignmentsList,
    listContext,
    navigate,
    navigationStore: consignmentNavigationStore,
    recordId: consignmentIdNumber,
    status,
  });
  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;
  const disabled = ids?.length === 0;

  // prepare steps for breadcrumbs

  const steps = [
    {
      label: "All Consignments",
      onClick: () =>
        navigate(`/consignment${location.search || "?status=all"}`),
    },
    ...(consignment
      ? [
          {
            label: consignment?.id,
            onClick: () =>
              navigate(
                buildConsignmentNavigationTargetUrl({
                  listContext,
                  recordId: consignment.id,
                  status,
                }),
              ),
          },
          {
            label: route?.name,
            onClick: () =>
              navigate(
                buildConsignmentNavigationTargetUrl({
                  activeSubPage: route?.href,
                  listContext,
                  recordId: consignment.id,
                  status,
                }),
              ),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state leaks into actions
  // here. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: consignmentId,
    selectedIds: ids,
    fallbacks: { search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    onClose: () => {
      formik?.resetForm();
    },
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const enableCreateJob = useMemo(
    () => consignment && consignment?.noOfConsignmentWorks > 0,
    [consignment],
  );

  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New Consignment",
        disabled: !hasPermission("CREATE_CONSIGNMENT"),
        modalProps: {
          body: NewConsignment,
          closeOnBackdrop: false,
          hideCloseButton: true,
          scale: "md",
        },
      },
      {
        disabled,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !consignment?.id && disabled,
        label: "Export Consignment Report",
        modalProps: {
          ...defaultActionProps,
          body: ExportActiveReport,
          optimise: true,
          type: "Consignment",
          ids: consignment?.id ? [consignment?.id] : ids,
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      ...(consignment?.id
        ? [
            {
              label: "Copy Description",
              modalProps: {
                title: "Copy description?",
                scale: "sm",
                description:
                  "Do you wish to copy the description from the consignment?",
                closeOnBackdrop: true,
                consignmentId: consignment?.id,
                body: CopyDescription,
              },
            },
            {
              label: "Create Extension",
              modalProps: {
                title: "Extend Consignment",
                scale: "sm",
                description: `Are you sure you wish to create an extension for Consignment: ${consignment?.id}?`,
                closeOnBackdrop: true,
                body: ExtendConsignment,
                consignment,
              },
              disabled: !hasPermission("UPDATE_CONSIGNMENT"),
            },
            {
              label: "Create Job",
              title: enableCreateJob
                ? "Create job"
                : "Works are required to be able to create job",
              disabled:
                !hasPermission("UPDATE_CONSIGNMENT") || !enableCreateJob,
              modalProps: {
                body: CreateJob,
                closeOnBackdrop: false,
                hideCloseButton: true,
                scale: "md",
                consignmentId: consignment?.id,
              },
            },
            {
              label: "Create Shipment",
              title: enableCreateJob
                ? "Create shipment"
                : "Works are required to be able to create shipment",
              disabled:
                !hasPermission("UPDATE_CONSIGNMENT") || !enableCreateJob,
              modalProps: {
                body: CreateShipment,
                closeOnBackdrop: false,
                hideCloseButton: true,
                scale: "md",
                consignmentId: consignment?.id,
              },
            },
            {
              label: "Create Invoice",
              title: enableCreateJob
                ? "Create invoice"
                : "Works are required to be able to create invoice",
              disabled:
                !hasPermission("UPDATE_CONSIGNMENT") || !enableCreateJob,
              modalProps: {
                body: CreateInvoice,
                closeOnBackdrop: false,
                hideCloseButton: true,
                scale: "md",
                consignmentId: consignment?.id,
              },
            },
          ]
        : []),
    ],
  };

  const searchModalProps = {
    body: ConsignmentSearch,
    closeOnBackdrop: true,
    scale: "fullscreen",
    isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    onSearchSubmitted: () => clearSelectionState(formik),
  };

  return (
    <ResponsivePrimaryNav>
      <div>
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex items-center gap-2 sm:gap-4 lg:justify-end">
        <SearchResultNavigation
          hasNavigationContext={hasNavigationContext}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          navigationLoading={navigationLoading}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationControls={showNavigationControls}
        />
        <Modal {...searchModalProps}>
          <Button
            label="Consignment Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} />
      </div>
    </ResponsivePrimaryNav>
  );
};

export default PrimaryNav;
